/**
 * useViewStorage - 視窗儲存功能
 * 處理視窗配置的持久化存儲和恢復
 */

/**
 * 座標位置介面
 */
export interface Position {
    x: number;
    y: number;
}

/**
 * 尺寸大小介面
 */
export interface Size {
    width: number;
    height: number;
}

/**
 * Snap 區域類型
 */
export type SnapZone = 'maximize' | 'left' | 'right' | null;

/**
 * Snap 前的配置介面
 */
export interface PreSnapConfig {
    position: Position;
    size: Size;
}

/**
 * 捲軸位置介面
 */
export interface ScrollPosition {
    top: number;
    left: number;
}

/**
 * 路由參數介面
 */
export interface RouteParams {
    fieldId?: string;
    groupId?: string;
    conversationId?: string;
    [key: string]: unknown;
}

/**
 * 視圖物件介面
 */
export interface View {
    id?: string;
    routeName?: string;
    routeParams?: RouteParams;
    title?: string;
    position: Position;
    size: Size;
    zIndex?: number;
    isPinned?: boolean;
    isMaximized?: boolean;
    isSnapped?: boolean;
    snapZone?: SnapZone;
    preSnapConfig?: PreSnapConfig | null;
    isCollapsed?: boolean;
    isTransparent?: boolean;
    scrollPosition?: ScrollPosition;
}

/**
 * 視圖配置介面（用於儲存）
 */
export interface ViewConfig {
    routeName?: string;
    routeParams?: RouteParams;
    title?: string;
    position?: Position;
    size?: Size;
    isPinned?: boolean;
    isMaximized?: boolean;
    isSnapped?: boolean;
    snapZone?: SnapZone;
    preSnapConfig?: PreSnapConfig | null;
    isCollapsed?: boolean;
    isTransparent?: boolean;
    scrollPosition?: ScrollPosition;
    order?: number;
}

/**
 * 視圖儲存資料介面
 */
export interface ViewStorageData {
    [key: string]: ViewConfig;
}

/**
 * 尺寸設定介面
 */
export interface SizeSettings {
    width: number;
    height: number;
}

/**
 * 尺寸儲存資料介面
 */
export interface SizeStorageData {
    [key: string]: SizeSettings;
}

/**
 * 建立視圖配置選項介面
 */
export interface CreateViewConfigOptions {
    /** 視圖索引 */
    viewIndex?: number;
    /** 下一個 zIndex */
    nextZIndex?: number;
    /** 預設寬度 */
    defaultWidth?: number;
    /** 預設高度 */
    defaultHeight?: number;
}

/**
 * 防抖儲存介面
 */
export interface DebouncedSave {
    /** 儲存函式 */
    save: (activeViews: View[], immediate?: boolean) => void;
    /** 取消函式 */
    cancel: () => void;
}

/**
 * 防抖捲軸儲存介面
 */
export interface DebouncedScrollSave {
    /** 儲存函式 */
    save: (view: View | null, scrollTop: number, scrollLeft: number) => void;
    /** 取消函式 */
    cancel: () => void;
}

// 預設儲存 key
const DEFAULT_STORAGE_KEY = 'multi-view-configs';
const DEFAULT_SIZE_STORAGE_KEY = 'multi-view-sizes';

/**
 * 取得視圖的儲存 key
 * @param view - 視圖物件
 * @returns 儲存 key
 */
export function getViewStorageKey(view: View): string {
    const routeName = view.routeName || '';
    const fieldId = view.routeParams?.fieldId || '';
    const groupId = view.routeParams?.groupId || '';
    const conversationId = view.routeParams?.conversationId || '';

    // 對於案場相關的 view，加入 fieldId
    // 對於聊天相關的 view，加入 groupId 和 conversationId
    if (fieldId) {
        return `${routeName}_field_${fieldId}`;
    } else if (groupId || conversationId) {
        return `${routeName}_chat_${groupId}_${conversationId}`;
    }
    return routeName;
}

/**
 * 從 localStorage 載入視圖配置
 * @param storageKey - 儲存 key
 * @returns 視圖配置
 */
export function loadViewsFromStorage(storageKey: string = DEFAULT_STORAGE_KEY): ViewStorageData {
    try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
            return JSON.parse(stored) as ViewStorageData;
        }
    } catch (error) {
        console.warn('[useViewStorage] 無法從 localStorage 載入視圖配置:', error);
    }
    return {};
}

/**
 * 保存視圖配置到 localStorage
 * @param activeViews - 活動視圖陣列
 * @param storageKey - 儲存 key
 */
export function saveViewsToStorageSync(activeViews: View[], storageKey: string = DEFAULT_STORAGE_KEY): void {
    try {
        const existingData = loadViewsFromStorage(storageKey);
        const viewsData: ViewStorageData = {};

        // 根據 zIndex 排序
        const sortedViews = [...activeViews].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

        sortedViews.forEach((view, index) => {
            const viewStorageKey = getViewStorageKey(view);
            const existingConfig = existingData[viewStorageKey] || {};

            viewsData[viewStorageKey] = {
                ...existingConfig,
                routeName: view.routeName,
                size: { ...view.size },
                position: { ...view.position },
                isPinned: view.isPinned || false,
                isTransparent: view.isTransparent || false,
                isMaximized: view.isMaximized || false,
                isCollapsed: view.isCollapsed || false,
                isSnapped: view.isSnapped || false,
                snapZone: view.snapZone || null,
                preSnapConfig: view.preSnapConfig || null,
                order: index
            };

            if (view.title) {
                viewsData[viewStorageKey].title = view.title;
            }

            if (view.routeParams && Object.keys(view.routeParams).length > 0) {
                viewsData[viewStorageKey].routeParams = view.routeParams;
            }
        });

        localStorage.setItem(storageKey, JSON.stringify(viewsData));
    } catch (error) {
        console.warn('[useViewStorage] 無法保存視圖配置到 localStorage:', error);
    }
}

/**
 * 建立防抖的儲存函式
 * @param storageKey - 儲存 key
 * @param delay - 延遲時間（毫秒）
 * @returns 防抖儲存物件
 */
export function createDebouncedSave(storageKey: string = DEFAULT_STORAGE_KEY, delay: number = 300): DebouncedSave {
    let timer: ReturnType<typeof setTimeout> | null = null;

    return {
        save(activeViews: View[], immediate: boolean = false): void {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }

            if (immediate) {
                saveViewsToStorageSync(activeViews, storageKey);
            } else {
                timer = setTimeout(() => {
                    saveViewsToStorageSync(activeViews, storageKey);
                    timer = null;
                }, delay);
            }
        },
        cancel(): void {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
        }
    };
}

/**
 * 保存視圖尺寸設定
 * @param view - 視圖物件
 * @param sizeStorageKey - 尺寸儲存 key
 */
export function saveSizeSettings(view: View | null, sizeStorageKey: string = DEFAULT_SIZE_STORAGE_KEY): void {
    if (!view || !view.routeName) return;

    try {
        const storageKey = getViewStorageKey(view);
        const stored = localStorage.getItem(sizeStorageKey);
        const sizeData: SizeStorageData = stored ? JSON.parse(stored) : {};

        sizeData[storageKey] = {
            width: view.size?.width || 420,
            height: view.size?.height || 500
        };

        localStorage.setItem(sizeStorageKey, JSON.stringify(sizeData));
    } catch (error) {
        console.warn('[useViewStorage] 保存尺寸設定失敗:', error);
    }
}

/**
 * 讀取視圖尺寸設定
 * @param storageKey - 視圖儲存 key
 * @param sizeStorageKey - 尺寸儲存 key
 * @returns 尺寸設定
 */
export function loadSizeSettings(storageKey: string, sizeStorageKey: string = DEFAULT_SIZE_STORAGE_KEY): SizeSettings | null {
    try {
        const stored = localStorage.getItem(sizeStorageKey);
        if (stored) {
            const sizeData: SizeStorageData = JSON.parse(stored);
            return sizeData[storageKey] || null;
        }
    } catch (error) {
        console.warn('[useViewStorage] 讀取尺寸設定失敗:', error);
    }
    return null;
}

/**
 * 保存捲軸位置
 * @param view - 視圖物件
 * @param storageKey - 儲存 key
 */
export function saveScrollPosition(view: View | null, storageKey: string = DEFAULT_STORAGE_KEY): void {
    if (!view) return;

    try {
        const stored = loadViewsFromStorage(storageKey);
        const viewStorageKey = getViewStorageKey(view);
        if (stored[viewStorageKey]) {
            stored[viewStorageKey].scrollPosition = view.scrollPosition || { top: 0, left: 0 };
            localStorage.setItem(storageKey, JSON.stringify(stored));
        }
    } catch (error) {
        console.warn('[useViewStorage] 保存捲軸位置失敗:', error);
    }
}

/**
 * 建立防抖的捲軸位置儲存
 * @param storageKey - 儲存 key
 * @param delay - 延遲時間（毫秒）
 * @returns 防抖捲軸儲存物件
 */
export function createDebouncedScrollSave(storageKey: string = DEFAULT_STORAGE_KEY, delay: number = 300): DebouncedScrollSave {
    let timer: ReturnType<typeof setTimeout> | null = null;

    return {
        save(view: View | null, scrollTop: number, scrollLeft: number): void {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }

            timer = setTimeout(() => {
                if (view) {
                    view.scrollPosition = {
                        top: scrollTop || 0,
                        left: scrollLeft || 0
                    };
                    saveScrollPosition(view, storageKey);
                }
                timer = null;
            }, delay);
        },
        cancel(): void {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
        }
    };
}

/**
 * 清除視圖儲存
 * @param storageKey - 儲存 key
 * @param sizeStorageKey - 尺寸儲存 key
 */
export function clearViewStorage(storageKey: string = DEFAULT_STORAGE_KEY, sizeStorageKey: string = DEFAULT_SIZE_STORAGE_KEY): void {
    try {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(sizeStorageKey);
    } catch (error) {
        console.warn('[useViewStorage] 清除儲存失敗:', error);
    }
}

/**
 * 建立視圖配置物件（用於恢復視圖）
 * @param config - 儲存的配置
 * @param options - 額外選項
 * @returns 視圖配置
 */
export function createViewConfig(config: ViewConfig, options: CreateViewConfigOptions = {}): View {
    const {
        viewIndex = 0,
        nextZIndex = 100,
        defaultWidth = 420,
        defaultHeight = 500
    } = options;

    const offsetX = 90 + (viewIndex * 30);
    const offsetY = 70 + (viewIndex * 30);
    const defaultPosition: Position = { x: offsetX, y: offsetY };
    const defaultSize: Size = { width: defaultWidth, height: defaultHeight };

    // 優先使用保存的位置
    const savedPosition: Position = (config.position?.x !== undefined && config.position?.y !== undefined)
        ? { ...config.position }
        : defaultPosition;

    const savedSize: Size = (config.size?.width !== undefined && config.size?.height !== undefined)
        ? { ...config.size }
        : defaultSize;

    return {
        routeName: config.routeName,
        routeParams: config.routeParams || {},
        title: config.title || config.routeName,
        position: savedPosition,
        size: savedSize,
        zIndex: nextZIndex,
        isCollapsed: config.isCollapsed || false,
        isMaximized: config.isMaximized || false,
        isTransparent: config.isTransparent || false,
        isPinned: config.isPinned || false,
        isSnapped: config.isSnapped || false,
        snapZone: config.snapZone || null,
        preSnapConfig: config.preSnapConfig || null,
        scrollPosition: config.scrollPosition || { top: 0, left: 0 }
    };
}

export default {
    getViewStorageKey,
    loadViewsFromStorage,
    saveViewsToStorageSync,
    createDebouncedSave,
    saveSizeSettings,
    loadSizeSettings,
    saveScrollPosition,
    createDebouncedScrollSave,
    clearViewStorage,
    createViewConfig
};
